import { FormControlLabel, Switch } from '@mui/material';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import React, { ReactNode, useState } from 'react';
import { Control, Controller, useForm } from 'react-hook-form';
import { SpeakerEntry } from '../../../../../../interfaces/pagebuilder';
import validationBuilder from '../../../../../../static/validations';
import { CustomTranslationForm } from '../../../../../common/CustomTranslationForm';
import VyButton from '../../../../../common/VyButton';
import VyModal from '../../../../../common/VyModal';
import VyTextField from '../../../../../common/VyTextField';
import VyUpload from '../../../../../common/VyUpload';

const VyRichTextEditor = dynamic(() => import('../../../../../common/VyRichTextEditor'), {
  ssr: false,
  loading: () => <p>loading...</p>,
});
interface NewSpeakersProps {
  onSpeakerCreated: (props: SpeakerEntry) => void;
  defaultValues?: SpeakerEntry;
  withHasModalOption?: boolean;
  isModal?: boolean;
  isModalOpen?: boolean;
  applyDefaultStyleOnEditor?: boolean;
  onCloseModal?: () => void;
}

const SpeakerFormContainer = ({
  isModal = false,
  open = false,
  onClose,
  children,
}: {
  isModal?: boolean;
  open?: boolean;
  onClose?: () => void;
  children?: ReactNode;
}) => {
  if (isModal)
    return (
      <VyModal onClose={() => onClose?.()} open={open} disableEnforceFocus>
        {() => <>{children}</>}
      </VyModal>
    );
  return <>{children}</>;
};

const SpeakerForm = ({
  onSpeakerCreated,
  defaultValues,
  withHasModalOption = true,
  applyDefaultStyleOnEditor = true,
  isModal,
  isModalOpen,
  onCloseModal,
}: NewSpeakersProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    control,
  } = useForm({ defaultValues, mode: 'all' });
  const { t } = useTranslation(['pagebuilder', 'common']);
  const validation = validationBuilder(t);
  const [img, setImg] = useState<string>(defaultValues?.image || '');

  function handleSpeakerCreated(props: SpeakerEntry) {
    props.image = img;
    reset();
    setImg('');
    onSpeakerCreated(props);
    onCloseModal?.();
  }

  return (
    <SpeakerFormContainer isModal={isModal} open={isModalOpen} onClose={onCloseModal}>
      <form onSubmit={handleSubmit(handleSpeakerCreated)} className="flex flex-col">
        <div className="flex flex-col p-4 overflow-scroll">
          <div className="flex flex-col mt-4 space-y-4" style={{ maxHeight: isModal && defaultValues ? '60vh' : '100%' }}>
            <input {...register('_id')} hidden />
            <input {...register('event')} hidden />

            <VyUpload
              randomExternalImageButton={false}
              acceptedFileTypes="image/*"
              showPreview={true}
              withCrop={true}
              cropAspectRatio={1}
              showCropButton={false}
              defaultFileId={img}
              onChange={(fileId) => {
                setImg(fileId || '');
              }}
            />

            <Controller
              control={control}
              name={`name`}
              rules={{
                minLength: { value: 3, message: t(`pagebuilder:entry-error-description-length`) },
                required: t(`pagebuilder:entry-error-title-required`) as string,
              }}
              render={({ field }) => <VyTextField {...field} label={t('common:name')} fullWidth />}
            />

            <CustomTranslationForm watch={watch} control={(control as unknown) as Control<Record<string, any>>} fieldName="nameTranslation" />
            <VyTextField register={register('position')} label="Position" fullWidth />
            <CustomTranslationForm watch={watch} control={(control as unknown) as Control<Record<string, any>>} fieldName="positionTranslation" />
            <Controller
              control={control}
              name="description"
              defaultValue={defaultValues?.description ?? ''}
              render={({ field: { onChange, value } }) => (
                <>
                  <VyRichTextEditor defaultValue={value} onBlur={(desc: string) => onChange(desc)} applyDefaultStyle={applyDefaultStyleOnEditor} />
                </>
              )}
            />
            {/* <CustomTranslationForm watch={watch} control={control} fieldName="descriptionTranslation" /> */}
            <VyTextField register={register('linkName')} label={t('common:Linkbezeichnung')} defaultValue={'Link'} fullWidth />
            <Controller
              control={control}
              rules={validation('urlWithoutProtocol')}
              name="url"
              render={({ field }) => <VyTextField {...field} label="URL" fullWidth />}
            />
            {withHasModalOption && (
              <Controller
                control={control}
                name="hasModal"
                defaultValue={true}
                render={({ field: { onChange, value } }) => {
                  return (
                    <FormControlLabel
                      className="w-full"
                      control={
                        <Switch
                          onChange={(e: any) => {
                            onChange(e.target.checked);
                          }}
                          checked={value}
                        />
                      }
                      label={t('pagebuilder:has-modal')}
                    />
                  );
                }}
              />
            )}
            {!(isModal && defaultValues) && (
              <>
                <VyButton isSaveButton style={{ marginTop: '1rem' }} type="submit">
                  {defaultValues ? t('pagebuilder:save-changes') : t('pagebuilder:add-new-entry')}
                </VyButton>
                {errors && Object.values(errors).map((error) => error && <p key={error.message}>{error.message}</p>)}
              </>
            )}
          </div>
        </div>

        {isModal && defaultValues && (
          <>
            <VyButton isSaveButton style={{ marginTop: '1rem' }} type="submit">
              {t('pagebuilder:save-changes')}
            </VyButton>
            {errors && Object.values(errors).map((error) => error && <p key={error.message}>{error.message}</p>)}
          </>
        )}
      </form>
    </SpeakerFormContainer>
  );
};

export default SpeakerForm;
